import { Target, Eye, Heart, Users, Award, Shield } from 'lucide-react';

const About = () => {
  const values = [
    {
      title: "Transparency",
      description: "Over empty promises - We believe in honest communication and realistic expectations.",
      icon: <Eye className="w-6 h-6" />
    },
    {
      title: "Practical Learning",
      description: "Over theoretical certificates - Real skills matter more than paper qualifications.",
      icon: <Target className="w-6 h-6" />
    },
    {
      title: "Community Growth",
      description: "Over individual success - We grow together, supporting each other's journeys.",
      icon: <Users className="w-6 h-6" />
    },
    {
      title: "Real Projects",
      description: "Over fake assignments - Hands-on experience with real-world problems.",
      icon: <Award className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 text-white">
            About GrowOn Community
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Building a trusted ecosystem where students grow into professionals with clarity and confidence.
          </p>
        </div>

        {/* Vision & Mission */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Vision */}
            <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-2xl p-8 border border-blue-800/50">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Our Vision</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">
                To build a trusted ecosystem where students grow into professionals with clarity and confidence.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-gradient-to-br from-green-900/30 to-blue-900/30 rounded-2xl p-8 border border-green-800/50">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Our Mission</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">
                To connect students with mentors, opportunities, and industry exposure through honest guidance and meaningful collaboration.
              </p>
            </div>
          </div>
        </div>

        {/* Why We Exist */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="bg-gray-800 rounded-2xl p-8">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mr-4">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Why GrowOn Exists</h2>
            </div>
            
            <div className="space-y-6">
              <p className="text-lg text-gray-300">
                Because students deserve <span className="text-white font-semibold">real direction</span>, not confusion.
              </p>
              <p className="text-lg text-gray-300">
                Because students deserve <span className="text-white font-semibold">clear expectations</span>, not noise.
              </p>
              <p className="text-lg text-gray-300">
                Because students deserve <span className="text-white font-semibold">honest guidance</span>, not false promises.
              </p>
            </div>

            <div className="mt-8 p-6 bg-blue-900/30 rounded-xl border border-blue-800">
              <p className="text-blue-300 text-center font-semibold">
                We exist to solve the gap between academic learning and real-world professional excellence.
              </p>
            </div>
          </div>
        </div>

        {/* Our Values */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold mb-12 text-center text-white">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-blue-500 transition-colors">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-4 text-white">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white">{value.title}</h3>
                </div>
                <p className="text-gray-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final Note */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center mr-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Final Note</h3>
                <p className="text-gray-400">Our commitment to students</p>
              </div>
            </div>
            <p className="text-lg text-gray-300 mb-6">
              GrowOn Community is built on <span className="text-white font-bold">people, proof, and progress</span>.
            </p>
            <div className="space-y-3">
              <p className="text-gray-400">No shortcuts.</p>
              <p className="text-gray-400">No hype.</p>
              <p className="text-gray-400">Just steady, honest growth.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;